/**
 * MypageHomePresenter
 * --
 */
import React from 'react';
import {
  Content,
  Row,
  Col,
  Title,
  Text,
  // Table, Button
} from 'components';
import { stringToMoneyFormat } from 'utils';
import { SideMenu } from '../components';
import { Empty } from 'antd';
import { encrypt } from 'utils/Crypto';

// import moment from 'moment';
import OrderListTable from '../components/OrderListTable/OrderListTable';
import MyItemList from '../components/ItemList/MyItemList';
// import OrderItem from '../components/OrderItem/OrderItem';
import InquiryList from '../components/InquiryList/InquiryList';
import more from 'assets/more.svg';
import more_w from 'assets/more_w.svg';
import rightarrow from 'assets/rightarrow.svg';
import card from 'assets/card.svg';
import point from 'assets/point.svg';
import coupon from 'assets/coupon.svg';
import review from 'assets/review.svg';
import Formatter from 'utils/Formatter';

const VIRTUAL_ACCOUNT_BANK = {
  '03': '기업은행',
  '04': '국민은행',
  '05': '하나은행',
  11: '농협중앙',
  20: '우리은행',
  23: 'SC제일은행',
  26: '신한은행',
  31: '대구은행',
  32: '부산은행',
  34: '광주은행',
  37: '전북은행',
  39: '경남은행',
  71: '우체국',
};

const getVirtualAccountInfo = (paymentInfo = {}) => ({
  bankName: VIRTUAL_ACCOUNT_BANK[paymentInfo?.authno],
  accountNumber: paymentInfo?.isscd,
});

const getVirtualAccountDepositStatus = (order = {}) => {
  const depositStatuses = (order?.parcel_service ?? [])
    .map((parcel) => parcel?.deposit_status)
    .filter(Boolean);

  if (depositStatuses.length) {
    if (depositStatuses.every((status) => status === 'COMPLETED')) {
      return '입금완료';
    }
    if (depositStatuses.some((status) => status === 'COMPLETED')) {
      return '일부 입금완료';
    }
    return '입금대기';
  }

  if (order?.payment_state === 'PENDING') return '입금대기';
  if (order?.payment_state === 'COMPLETED') return '입금완료';
  return '입금상태 확인 필요';
};

const MypageHomePresenter = (props) => {
  /* ===== Props ===== */
  const {
    navigate,
    user,
    recentOrderList,
    orderList,
    wishlist,
    inquiries,
    screenSize,
    grades,
    totalPrice,
    totalCount,
    setting,
  } = props;

  const goToPage = (page) => {
    navigate(page);
  };

  /* ===== VARIABLE ===== */
  const styles = {
    titleLayout: {
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      paddingBottom: 24,
      paddingTop: 8,
      borderBottom: '2px solid var(--black-color)',
    },

    title: {
      fontSize: screenSize >= 768 ? 24 : 16,
      fontWeight: 700,
      color: 'var(--black-color)',
      fontFamily: 'Pretendard',
    },
    listLayout: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: screenSize >= 768 ? '60%' : '100%',
      backgroundColor: 'var(--white-color)',
      borderRadius: '2px',
    },
    itemLayout: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      width: '25%',
      borderRight: '2px solid #eee',
      height: screenSize >= 768 ? 144 : screenSize >= 500 ? 120 : 90,
    },
    fontResponse32: {
      fontSize: screenSize >= 768 ? 32 : 20,
    },
    fontResponse28: {
      fontSize: screenSize >= 768 ? 28 : 18,
    },
    fontResponse24: {
      fontSize: screenSize >= 768 ? 24 : 16,
    },
    fontResponse16: {
      fontSize: screenSize >= 768 ? 16 : 14,
    },
  };

  /* ===== PROPS ===== */
  /* ===== STATE ===== */

  /* 재입고 알림 내역 */
  // const restockList = [
  //   {
  //     created_at: new Date(),
  //     product_name: '시그니처블랜드 617th 에비뉴 200g',
  //     place_name: 'JMCOFFEE',
  //     product_data: '분쇄안함 / 200g / 10개',
  //     src: Product,
  //     type: 'active', // "active" = 알림 해제와 삭제 버튼 표시
  //   },
  //   {
  //     created_at: new Date(),
  //     product_name: '시그니처블랜드 617th 에비뉴 200g',
  //     place_name: 'JMCOFFEE',
  //     product_data: '분쇄안함 / 200g / 10개',
  //     src: Product,
  //     type: 'inactive', // "inactive" = 알림 재신청 버튼 표시
  //   },
  // ];

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /**
   * 다음달 예상 등급
   * --
   */
  const NextGrade = React.useMemo(() => {
    if (!setting)
      return {
        current: grades[0],
        next: grades[0],
      };

    const { grade_standard } = setting;
    const { variable } = grades;

    // 등급 기준 금액에 따른 내림차순 정렬
    const sortAmountGrades = variable
      .slice()
      .sort((a, b) => b.grade_condition_amount - a.grade_condition_amount);
    // 등급 기준 건수에 따른 내림차순 정렬
    const sortCountGrades = variable
      .slice()
      .sort((a, b) => b.transaction_count - a.transaction_count);

    let fid = 0,
      standard = sortAmountGrades;
    switch (grade_standard) {
      case 'AMOUNT':
        fid = sortAmountGrades.findIndex(
          (grade) => grade.grade_condition_amount <= totalPrice,
        );
        break;
      case 'COUNT':
        standard = sortCountGrades;
        fid = sortCountGrades.findIndex(
          (grade) => grade.transaction_count <= totalCount,
        );
        break;
      case 'AMOUNT_AND_COUNT':
        fid = sortAmountGrades.findIndex(
          (grade) =>
            grade.grade_condition_amount <= totalPrice &&
            grade.transaction_count <= totalCount,
        );
        break;
      case 'AMOUNT_OR_COUNT':
        fid = sortAmountGrades.findIndex(
          (grade) =>
            grade.grade_condition_amount <= totalPrice ||
            grade.transaction_count <= totalCount,
        );
        break;
      default:
        break;
    }

    return {
      current: standard[fid],
      next: fid === 0 ? null : standard[fid - 1],
    };
  }, [totalPrice, totalCount, grades, setting]);

  /* ===== RENDER ===== */
  return (
    <Content
      maxWidth={1820}
      padding={screenSize >= 768 ? '120px 0px' : '60px 0'}
    >
      <Row gutter={16}>
        <Col x={0} md={1} xl={2}></Col>
        <Col x={24} md={22} xl={20}>
          <Content
            maxWidth={'100%'}
            backgroundColor="var(--black-color)"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              height: screenSize >= 1336 ? 288 : 'auto',
              padding:
                screenSize >= 900
                  ? '64px 120px'
                  : screenSize >= 768
                    ? '50px 60px'
                    : '40px 20px',
              flexDirection: screenSize >= 768 ? 'unset' : 'column',
              gap: screenSize >= 1336 ? 'unset' : '20px',
            }}
          >
            {/* 최상단 좌측 컨텐츠 시작 */}
            <div
              style={{
                display: 'flex',
                //flexDirection: screenSize >= 768 ? 'unset' : 'column',
                //rowGap: 10,
                //alignItems: screenSize >= 768 ? 'center' : 'start',
                alignItems: 'center',
                width: screenSize >= 1336 ? '50%' : '100%',
              }}
            >
              <div
                style={{
                  borderRadius: '50%',
                  border: '2px solid var(--white-color)',
                  width: screenSize >= 768 ? 100 : 80,
                  height: screenSize >= 768 ? 100 : 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: screenSize >= 768 ? 28 : 15,
                }}
              >
                <Text
                  size={screenSize >= 768 ? 28 : 20}
                  className={'dela-gothic'}
                  color={'var(--white-color)'}
                >
                  {user?.grade?.grade_name?.[0]}
                </Text>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: screenSize >= 768 ? 'center' : 'start',
                    marginBottom: 15,
                    flexDirection: screenSize >= 768 ? 'unset' : 'column',
                  }}
                >
                  <Text
                    size={screenSize >= 768 ? 28 : 20}
                    className={'dela-gothic'}
                    color={'var(--white-color)'}
                    style={{
                      minWidth: 'fit-content',
                    }}
                  >
                    {user?.grade?.grade_name}
                  </Text>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 'fit-content',
                    }}
                  >
                    <Text
                      weight={700}
                      color={'var(--white-color)'}
                      style={{
                        fontSize: screenSize >= 768 ? 32 : 20,
                        paddingLeft: screenSize >= 768 ? 12 : 0,
                        webkitLineClamp: 'unset',
                      }}
                    >
                      {user?.user_name}
                      <span
                        style={{
                          fontSize: screenSize >= 768 ? 32 : 20,
                          color: 'var(--white-color)',
                          fontWeight: 700,
                        }}
                      >
                        님
                      </span>
                    </Text>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <img
                        src={more_w}
                        alt="more icon"
                        style={{
                          cursor: 'pointer',
                          height: screenSize >= 768 ? 19 : 13,
                          marginLeft: 10,
                        }}
                        onClick={() => goToPage('/mypage/rate')}
                      />
                    </div>
                  </div>
                </div>
                {NextGrade.next ? (
                  [setting?.grade_standard].map((standard) => {
                    const remainPrice =
                      NextGrade.next.grade_condition_amount - totalPrice;
                    const remainCount =
                      NextGrade.next.transaction_count - totalCount;

                    switch (standard) {
                      case 'AMOUNT':
                        return (
                          <div
                            key={`setting-${standard}`}
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              color: 'var(--white-color)',
                              marginBottom: 2,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Text
                                color={'var(--white-color)'}
                                style={{
                                  ...styles.fontResponse16,
                                }}
                              >
                                {stringToMoneyFormat(remainPrice)}
                              </Text>
                              <span
                                style={{
                                  ...styles.fontResponse16,
                                  padding: '0 5px 1px 0',
                                  color: 'var(--white-color)',
                                }}
                              >
                                원 추가 구매 시
                              </span>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Text
                                color={'var(--white-color)'}
                                style={{
                                  ...styles.fontResponse16,
                                }}
                              >
                                {NextGrade.next?.grade_name}
                              </Text>
                              <span
                                style={{
                                  ...styles.fontResponse16,
                                  padding: '0 0 1px 5px',
                                  color: 'var(--white-color)',
                                }}
                              >
                                달성
                              </span>
                            </div>
                          </div>
                        );
                      case 'COUNT':
                        return (
                          <div
                            key={`setting-${standard}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--white-color)',
                              marginBottom: 2,
                            }}
                          >
                            <Text
                              color={'var(--white-color)'}
                              style={{
                                ...styles.fontResponse16,
                              }}
                            >
                              {stringToMoneyFormat(remainCount)}
                            </Text>
                            <span
                              style={{
                                ...styles.fontResponse16,
                                padding: '0 5px 1px 0',
                                color: 'var(--white-color)',
                              }}
                            >
                              건 추가 구매 시
                            </span>
                            <Text
                              color={'var(--white-color)'}
                              style={{
                                ...styles.fontResponse16,
                              }}
                            >
                              {NextGrade.next?.grade_name}
                            </Text>
                            <span
                              style={{
                                ...styles.fontResponse16,
                                padding: '0 0 1px 5px',
                                color: 'var(--white-color)',
                              }}
                            >
                              달성
                            </span>
                          </div>
                        );
                      case 'AMOUNT_AND_COUNT':
                        return (
                          <div
                            key={`setting-${standard}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--white-color)',
                              marginBottom: 2,
                            }}
                          >
                            <Text
                              color={'var(--white-color)'}
                              style={{
                                ...styles.fontResponse16,
                              }}
                            >
                              {stringToMoneyFormat(
                                remainPrice > 0 ? remainPrice : 0,
                              )}
                            </Text>
                            <span
                              style={{
                                ...styles.fontResponse16,
                                padding: '0 5px 1px 0',
                                color: 'var(--white-color)',
                              }}
                            >
                              원,
                            </span>
                            <Text
                              color={'var(--white-color)'}
                              style={{
                                ...styles.fontResponse16,
                              }}
                            >
                              {stringToMoneyFormat(
                                remainCount > 0 ? remainCount : 0,
                              )}
                            </Text>
                            <span
                              style={{
                                ...styles.fontResponse16,
                                padding: '0 5px 1px 0',
                                color: 'var(--white-color)',
                              }}
                            >
                              건 추가 구매 시
                            </span>
                            <Text
                              color={'var(--white-color)'}
                              style={{
                                ...styles.fontResponse16,
                              }}
                            >
                              {NextGrade.next?.grade_name}
                            </Text>
                            <span
                              style={{
                                ...styles.fontResponse16,
                                padding: '0 0 1px 5px',
                                color: 'var(--white-color)',
                              }}
                            >
                              달성
                            </span>
                          </div>
                        );
                      case 'AMOUNT_OR_COUNT':
                        return (
                          <div
                            key={`setting-${standard}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--white-color)',
                              marginBottom: 2,
                            }}
                          >
                            <Text
                              color={'var(--white-color)'}
                              style={{
                                ...styles.fontResponse16,
                              }}
                            >
                              {stringToMoneyFormat(remainPrice)}
                            </Text>
                            <span
                              style={{
                                ...styles.fontResponse16,
                                padding: '0 5px 1px 0',
                                color: 'var(--white-color)',
                              }}
                            >
                              원 또는
                            </span>
                            <Text
                              color={'var(--white-color)'}
                              style={{
                                ...styles.fontResponse16,
                              }}
                            >
                              {stringToMoneyFormat(remainCount)}
                            </Text>
                            <span
                              style={{
                                ...styles.fontResponse16,
                                padding: '0 5px 1px 0',
                                color: 'var(--white-color)',
                              }}
                            >
                              건 추가 구매 시
                            </span>
                            <Text
                              color={'var(--white-color)'}
                              style={{
                                ...styles.fontResponse16,
                              }}
                            >
                              {NextGrade.next?.grade_name}
                            </Text>
                            <span
                              style={{
                                ...styles.fontResponse16,
                                padding: '0 0 1px 5px',
                                color: 'var(--white-color)',
                              }}
                            >
                              달성
                            </span>
                          </div>
                        );
                      default:
                        return (
                          <React.Fragment key={`setting-${standard}`}>
                            <Text
                              color={'var(--white-color)'}
                              style={{
                                ...styles.fontResponse16,
                              }}
                            >
                              -
                            </Text>
                          </React.Fragment>
                        );
                    }
                  })
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--black-color)',
                    }}
                  >
                    <Text
                      style={{
                        ...styles.fontResponse16,
                        padding: '0 0 1px 5px',
                        color: 'var(--white-color)',
                      }}
                    >
                      최고등급달성!
                    </Text>
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--white-color)',
                    fontSize: 12,
                  }}
                >
                  <Text color={'var(--white-color)'} size={12}>
                    {Formatter.unixToDate(user?.grade_start_date, 'YYYY.MM.DD')}
                  </Text>
                  <span
                    style={{
                      color: 'var(--white-color)',
                      fontSize: 16,
                      padding: '0 5px',
                    }}
                  >
                    ~
                  </span>
                  <Text color={'var(--white-color)'} size={12}>
                    {Formatter.unixToDate(user?.grade_end_date, 'YYYY.MM.DD')}
                  </Text>
                </div>
              </div>
            </div>
            {/* 최상단 좌측 컨텐츠 끝 */}

            {/* 최상단 우측 컨텐츠 시작 */}
            <div
              style={{
                ...styles.listLayout,
                width: screenSize >= 1336 ? '50%' : '100%',
                minWidth: screenSize >= 1336 ? 305 : 'unset',
              }}
            >
              <div
                onClick={() => navigate(`/mypage/pay`)}
                style={{ ...styles.itemLayout, cursor: 'pointer' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={card}
                    alt="card icon"
                    style={{ height: 19, marginBottom: 5 }}
                  />
                  <Text size={14} color={'var(--dark-gray-color)'}>
                    JM페이
                  </Text>
                </div>
                <div>
                  <Text
                    size={screenSize >= 768 ? 20 : 14}
                    weight={700}
                    color={'var(--black-color)'}
                  >
                    {stringToMoneyFormat(user?.jm_pay)}
                    <span
                      style={{
                        fontSize: screenSize >= 768 ? 18 : 14,
                        color: 'var(--black-color)',
                        fontWeight: 700,
                      }}
                    >
                      원
                    </span>
                  </Text>
                </div>
              </div>

              <div
                onClick={() => navigate(`/mypage/point`)}
                style={{ ...styles.itemLayout, cursor: 'pointer' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={point}
                    alt="point icon"
                    style={{ height: 19, marginBottom: 5 }}
                  />
                  <Text size={14} color={'var(--dark-gray-color)'}>
                    포인트
                  </Text>
                </div>
                <div>
                  <Text
                    size={screenSize >= 768 ? 20 : 14}
                    weight={700}
                    color={'var(--black-color)'}
                  >
                    {stringToMoneyFormat(user?.point)}
                    <span
                      style={{
                        fontSize: screenSize >= 768 ? 18 : 14,
                        color: 'var(--black-color)',
                        fontWeight: 700,
                      }}
                    >
                      P
                    </span>
                  </Text>
                </div>
              </div>
              <div
                onClick={() => navigate(`/mypage/coupon`)}
                style={{ ...styles.itemLayout, cursor: 'pointer' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={coupon}
                    alt="coupon icon"
                    style={{ height: 19, marginBottom: 5 }}
                  />
                  <Text size={14} color={'var(--dark-gray-color)'}>
                    쿠폰
                  </Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Text
                    size={screenSize >= 768 ? 20 : 14}
                    weight={700}
                    color={'var(--black-color)'}
                  >
                    {stringToMoneyFormat(user?.coupons)}
                    <span
                      style={{
                        fontSize: screenSize >= 768 ? 20 : 14,
                        color: 'var(--black-color)',
                        fontWeight: 700,
                      }}
                    >
                      장
                    </span>
                  </Text>
                </div>
              </div>
              <div
                onClick={() => navigate(`/mypage/review`)}
                style={{
                  ...styles.itemLayout,
                  borderRight: 'none',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={review}
                    alt="review icon"
                    style={{ height: 19, marginBottom: 5 }}
                  />
                  <Text size={14} color={'var(--dark-gray-color)'}>
                    리뷰
                  </Text>
                </div>
                <div>
                  <Text
                    size={screenSize >= 768 ? 20 : 14}
                    weight={700}
                    color={'var(--black-color)'}
                  >
                    {stringToMoneyFormat(user?.reviews)}
                    <span
                      style={{
                        fontSize: screenSize >= 768 ? 18 : 14,
                        color: 'var(--black-color)',
                        fontWeight: 700,
                      }}
                    >
                      개
                    </span>
                  </Text>
                </div>
              </div>
            </div>
            {/* 최상단 우측 컨텐츠 끝 */}
          </Content>
        </Col>
        <Col x={0} md={1} xl={2}></Col>
      </Row>

      <Row gutter={25} style={{ marginTop: screenSize >= 768 ? 100 : 60 }}>
        <Col x={0} md={1} xl={2}></Col>
        <Col x={0} md={6} xl={4}>
          <SideMenu />
        </Col>
        <Col x={24} md={16} xl={16}>
          <Content
            maxWidth={'100%'}
            padding={
              screenSize >= 1200
                ? '0 0 0 95px'
                : screenSize >= 768
                  ? '0 0 0 20px'
                  : '0'
            }
          >
            {/* 주문/배송 조회 */}
            <Content maxWidth={'100%'} padding={0}>
              <div
                style={{
                  ...styles.titleLayout,
                }}
              >
                <Title style={{ ...styles.title }}>주문/배송 현황</Title>
                <img
                  src={more}
                  alt="more icon"
                  style={{ cursor: 'pointer' }}
                  onClick={() => goToPage('/mypage/order')}
                />
              </div>

              <div
                style={{
                  padding: '32px 0px',
                  borderBottom: '1px solid var(--light-gray-color)',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <ul
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '78%',
                  }}
                >
                  {orderList?.map((_item, _index) => (
                    <>
                      <li
                        key={`order_${_index}`}
                        style={{
                          width: '10%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            width: screenSize >= 768 ? 84 : 60,
                            height: screenSize >= 768 ? 84 : 60,
                            borderRadius: '50%',
                            background: 'var(--black-color)',
                            display: 'flex',
                            marginBottom: 16,
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Text
                            size={screenSize >= 768 ? 24 : 16}
                            weight={700}
                            color={'var(--white-color)'}
                          >
                            {_item.value}
                          </Text>
                        </div>
                        <Text
                          style={{
                            textAlign: 'center',
                            width: 70,
                            fontSize: screenSize >= 768 ? 16 : 14,
                          }}
                        >
                          {_item.title}
                        </Text>
                      </li>

                      {_index + 1 !== orderList.length && (
                        <li
                          style={{
                            marginBottom: 40,
                          }}
                        >
                          <img src={rightarrow} alt="rightarrow icon" />
                        </li>
                      )}
                    </>
                  ))}
                </ul>
              </div>
            </Content>
            {/* 주문 배송조회 끝 */}

            {/* 주문내역 */}
            <Content
              maxWidth={'100%'}
              padding={0}
              style={{
                marginTop: screenSize >= 768 ? 140 : 60,
              }}
            >
              <div style={{ ...styles.titleLayout, borderBottom: 0 }}>
                <Title style={{ ...styles.title }}>최근 주문내역</Title>
                <img
                  src={more}
                  alt="more icon"
                  style={{ cursor: 'pointer' }}
                  onClick={() => goToPage('/mypage/order')}
                />
              </div>
              {recentOrderList.length ? (
                recentOrderList.map((group) => {
                  const virtualAccountInfo = getVirtualAccountInfo(
                    group?.payment_info,
                  );

                  return (
                    <React.Fragment key={`shop-order-${group.shop_order_id}`}>
                      <OrderListTable
                        orders={[group]}
                        detailText="주문상세"
                        onDetailClick={(state) =>
                          navigate(
                            `/mypage/order/${encodeURIComponent(
                              encrypt(state.shop_order_id),
                            )}`,
                          )
                        }
                        // onSubmit={onOrderStatus}
                        showExpectedCycle={
                          group?.product_information[0]?.type === 'SUBSCRIPTION'
                            ? true
                            : false
                        }
                        screenSize={screenSize}
                      />
                      {group?.payment_method === 'VIRTUAL_ACCOUNT' ? (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: screenSize >= 500 ? 'row' : 'column',
                            gap: screenSize >= 500 ? 32 : 6,
                            padding: '14px 16px',
                            marginBottom: 20,
                            backgroundColor: '#f7f7f7',
                          }}
                        >
                          <Text size={14}>
                            입금은행:{' '}
                            <strong>
                              {virtualAccountInfo.bankName ?? '-'}
                            </strong>
                          </Text>
                          <Text size={14}>
                            계좌번호:{' '}
                            <strong>
                              {virtualAccountInfo.accountNumber ?? '-'}
                            </strong>
                          </Text>
                          <Text size={14}>
                            입금상태:{' '}
                            <strong>
                              {getVirtualAccountDepositStatus(group)}
                            </strong>
                          </Text>
                        </div>
                      ) : null}
                    </React.Fragment>
                  );
                })
              ) : (
                <div style={styles.emptyState}>
                  <Empty description={'주문 내역이 없습니다.'} />
                </div>
              )}
            </Content>
            {/* 주문내역 끝 */}

            {/* 위시리스트 */}
            <Content
              className="my_item_list"
              maxWidth={'100%'}
              padding={0}
              style={{ marginTop: screenSize >= 768 ? 140 : 60 }}
            >
              <div
                style={{
                  ...styles.titleLayout,
                }}
              >
                <Title style={{ ...styles.title }}>위시리스트</Title>
                <img
                  src={more}
                  alt="more icon"
                  style={{ cursor: 'pointer' }}
                  onClick={() => goToPage('/mypage/wishlist/wishlist')}
                />
              </div>
              <MyItemList
                title="위시리스트"
                items={wishlist}
                styles={styles}
                maxItems={screenSize >= 768 ? 5 : 4} // 최대 5개만 표시
              />
            </Content>
            {/* 위시리스트 끝 */}

            {/* 최근 본 상품 */}
            {/* <Content
              className="my_item_list"
              maxWidth={'100%'}
              padding={0}
              style={{ marginTop: 140 }}
            >
              <div
                style={{
                  ...styles.titleLayout,
                }}
              >
                <Title style={{ ...styles.title }}>최근 본 상품</Title>
                <img
                  src={more}
                  alt="more icon"
                  style={{ cursor: 'pointer' }}
                  onClick={() => goToPage('/mypage/wishlist/recentlysaw')}
                />
              </div>
              <MyItemList
                title="최근 본 상품"
                items={myItemList}
                styles={styles}
                maxItems={5} // 최대 5개만 표시
              />
            </Content> */}
            {/* 최근 본 상품 끝 */}

            {/* 재입고 알림 내역 */}
            {/* <Content maxWidth={'100%'} padding={0} style={{ marginTop: 140 }}>
              <div style={{ ...styles.titleLayout }}>
                <Title style={{ ...styles.title }}>재입고 알림 내역</Title>
                <img
                  src={more}
                  alt="more icon"
                  style={{ cursor: 'pointer' }}
                  onClick={() => goToPage('/mypage/restock')}
                />
              </div>
              <Table
                data={restockList}
                pagination={false}
                columns={[
                  {
                    key: 'row',
                    render: (item, record) => (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          padding: '24px 0',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '50%',
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 16,
                              width: '20%',
                              textAlign: 'start',
                            }}
                          >
                            {moment(record.created_at).format('YYYY.MM.DD')}
                          </Text>
                          <OrderItem
                            placeName={record.place_name}
                            productName={record.product_name}
                            src={record.src}
                            productData={record.product_data}
                            style={{
                              width: '75%',
                              padding: '32px 0',
                            }}
                          />
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 24,
                            width: '35%',
                          }}
                        >
                          {record.type === 'active' ? (
                            <>
                              <Button
                                style={{
                                  fontWeight: 700,
                                  border: '1px solid var(--black-color)',
                                  backgroundColor: 'transparent',
                                  color: 'var(--black-color)',
                                  borderRadius: 2,
                                  width: '47%',
                                  height: 48,
                                }}
                                onClick={() =>
                                  alert(`알림 해제 for ${record.product_name}`)
                                }
                              >
                                알림 해제
                              </Button>
                              <Button
                                style={{
                                  fontWeight: 700,
                                  border: '1px solid var(--black-color)',
                                  backgroundColor: 'transparent',
                                  color: 'var(--black-color)',
                                  borderRadius: 2,
                                  width: '47%',
                                  height: 48,
                                }}
                                onClick={() =>
                                  alert(`삭제 for ${record.product_name}`)
                                }
                              >
                                삭제
                              </Button>
                            </>
                          ) : (
                            <Button
                              style={{
                                fontWeight: 700,
                                border: '1px solid var(--black-color)',
                                backgroundColor: 'transparent',
                                color: 'var(--black-color)',
                                borderRadius: 2,
                                width: '100%',
                                height: 48,
                              }}
                              onClick={() =>
                                alert(`알림 재신청 for ${record.product_name}`)
                              }
                            >
                              알림 재신청
                            </Button>
                          )}
                        </div>
                      </div>
                    ),
                    style: {
                      width: '100%',
                      textAlign: 'start',
                    },
                  },
                ]}
                headerStyle={{
                  display: 'none',
                }}
                bordered={false}
                hovered={false}
                minHeight={restockList.length ? 'auto' : 200}
              />
            </Content> */}
            {/* 재입고 알림 내역 끝 */}

            {/* Q&A 와 1:1 문의 가로배치 */}
            <Content
              maxWidth={'100%'}
              padding={0}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: screenSize >= 768 ? 140 : 60,
                flexDirection: screenSize >= 768 ? 'unset' : 'column',
                gap: screenSize >= 768 ? 0 : '40px',
              }}
            >
              <div style={{ width: screenSize >= 768 ? '47%' : '100%' }}>
                <div style={{ ...styles.titleLayout }}>
                  <Title style={{ ...styles.title }}>상품 문의</Title>
                  <img
                    src={more}
                    alt="more icon"
                    style={{ cursor: 'pointer' }}
                    onClick={() => goToPage('/mypage/QnA')}
                  />
                </div>
                <InquiryList
                  inquiryList={inquiries.PRODUCT.slice(0, 2)}
                  emptyMessage="등록된 Q&A가 없습니다"
                  styles={styles}
                />
              </div>

              <div style={{ width: screenSize >= 768 ? '47%' : '100%' }}>
                <div style={{ ...styles.titleLayout }}>
                  <Title style={{ ...styles.title }}>1:1 문의</Title>
                  <img
                    src={more}
                    alt="more icon"
                    style={{ cursor: 'pointer' }}
                    onClick={() => goToPage('/cs/inquire')}
                  />
                </div>
                <InquiryList
                  inquiryList={inquiries.ONEINQUIRY.slice(0, 2)}
                  emptyMessage="등록된 1:1문의가 없습니다"
                  styles={styles}
                />
              </div>
            </Content>
          </Content>
        </Col>
        <Col x={0} md={1} xl={2}></Col>
      </Row>
    </Content>
  );
};

export default MypageHomePresenter;
